import { Graphics } from 'pixi.js';
import type { ParallaxLayers } from '../stage-scene';

export interface TreeNode { gfx: Graphics; brightness: number; phase: number }
export interface Vine { gfx: Graphics; baseX: number; topY: number; length: number; phase: number; sway: number }

export function buildTrees(layers: ParallaxLayers, W: number, H: number): TreeNode[] {
    const treeNodes: TreeNode[] = [];
    const treePositions = [
        { x: W * 0.03, trunkW: 130, canopyW: 350, canopyH: 320 },
        { x: W * 0.22, trunkW: 100, canopyW: 300, canopyH: 280 },
        { x: W * 0.42, trunkW: 160, canopyW: 400, canopyH: 350 },
        { x: W * 0.62, trunkW: 110, canopyW: 320, canopyH: 290 },
        { x: W * 0.82, trunkW: 140, canopyW: 370, canopyH: 330 },
    ];

    for (const tree of treePositions) {
        const gfx = new Graphics();
        const baseY = H * 0.8;
        const topY = H * 0.05;
        const bw = tree.trunkW;
        const tw = tree.trunkW * 0.55;

        // Gnarled trunk — irregular silhouette with quadratic curves
        const trunkLeft: [number, number][] = [
            [tree.x - bw / 2, baseY],
            [tree.x - bw * 0.52 + Math.random() * 8, baseY * 0.7],
            [tree.x - bw * 0.38, baseY * 0.5],
            [tree.x - tw * 0.55 - Math.random() * 6, topY + tree.canopyH * 0.3],
        ];
        const trunkRight: [number, number][] = [
            [tree.x + tw * 0.55 + Math.random() * 6, topY + tree.canopyH * 0.3],
            [tree.x + bw * 0.4, baseY * 0.5],
            [tree.x + bw * 0.48 + Math.random() * 8, baseY * 0.7],
            [tree.x + bw / 2, baseY],
        ];

        gfx.moveTo(trunkLeft[0][0], trunkLeft[0][1]);
        for (let i = 1; i < trunkLeft.length; i++) {
            const cpx = (trunkLeft[i - 1][0] + trunkLeft[i][0]) / 2 + (Math.random() - 0.5) * 12;
            const cpy = (trunkLeft[i - 1][1] + trunkLeft[i][1]) / 2;
            gfx.quadraticCurveTo(cpx, cpy, trunkLeft[i][0], trunkLeft[i][1]);
        }
        for (let i = 0; i < trunkRight.length; i++) {
            const cpx = i === 0
                ? (trunkLeft[trunkLeft.length - 1][0] + trunkRight[0][0]) / 2
                : (trunkRight[i - 1][0] + trunkRight[i][0]) / 2 + (Math.random() - 0.5) * 12;
            const cpy = i === 0
                ? trunkRight[0][1] - 10
                : (trunkRight[i - 1][1] + trunkRight[i][1]) / 2;
            gfx.quadraticCurveTo(cpx, cpy, trunkRight[i][0], trunkRight[i][1]);
        }
        gfx.closePath();
        gfx.fill({ color: 0x0a1508, alpha: 0.75 });

        // Knot holes — 2 dark ellipses on trunk
        for (let k = 0; k < 2; k++) {
            const kx = tree.x + (Math.random() - 0.5) * bw * 0.25;
            const ky = baseY * (0.4 + k * 0.2) + Math.random() * 40;
            gfx.ellipse(kx, ky, 6 + Math.random() * 4, 8 + Math.random() * 5);
            gfx.fill({ color: 0x030805, alpha: 0.6 });
        }

        // Bark lines — 6 lines for texture
        for (let b = 0; b < 6; b++) {
            const bx = tree.x - bw * 0.35 + b * (bw * 0.14);
            gfx.moveTo(bx, baseY);
            gfx.quadraticCurveTo(
                bx + (Math.random() - 0.5) * 10, (baseY + topY + tree.canopyH * 0.4) / 2,
                bx + (Math.random() - 0.5) * 8, topY + tree.canopyH * 0.4,
            );
            gfx.stroke({ color: 0x0d1a0a, alpha: 0.3, width: 1 });
        }

        // Buttress roots — 5-7 spreading roots
        const rootCount = 5 + Math.floor(Math.random() * 3);
        for (let r = 0; r < rootCount; r++) {
            const rootDir = r < rootCount / 2 ? -1 : 1;
            const rootLen = 80 + Math.random() * 40;
            const rootStartX = tree.x + rootDir * bw * (0.3 + Math.random() * 0.15);
            const rootEndX = rootStartX + rootDir * rootLen;
            const curveBack = Math.random() > 0.5;
            gfx.moveTo(rootStartX, baseY);
            gfx.quadraticCurveTo(
                (rootStartX + rootEndX) / 2 + rootDir * 10, baseY - 15 - Math.random() * 10,
                rootEndX, curveBack ? baseY + 8 : baseY + 3,
            );
            gfx.stroke({ color: 0x0a1508, alpha: 0.55, width: 4 + Math.random() });
        }

        // Branch network — 4-6 major branches
        const branchCount = 4 + Math.floor(Math.random() * 3);
        for (let br = 0; br < branchCount; br++) {
            const branchY = topY + tree.canopyH * (0.15 + br * 0.12) + Math.random() * 30;
            const branchDir = br % 2 === 0 ? -1 : 1;
            const branchLen = 80 + Math.random() * 100;
            const startX = tree.x + branchDir * tw * 0.3;
            const endX = startX + branchDir * branchLen;
            const endY = branchY - 20 + Math.random() * 40;

            // Main branch
            gfx.moveTo(startX, branchY);
            gfx.quadraticCurveTo(
                (startX + endX) / 2, branchY - 15 + Math.random() * 10,
                endX, endY,
            );
            gfx.stroke({ color: 0x0a1508, alpha: 0.5, width: 4 - br * 0.3 });

            // 2-3 sub-branches
            const subCount = 2 + Math.floor(Math.random() * 2);
            for (let s = 0; s < subCount; s++) {
                const t = 0.4 + s * 0.2 + Math.random() * 0.1;
                const sx = startX + (endX - startX) * t;
                const sy = branchY + (endY - branchY) * t;
                const subLen = 30 + Math.random() * 40;
                const subDir = Math.random() > 0.5 ? 1 : -1;
                gfx.moveTo(sx, sy);
                gfx.quadraticCurveTo(
                    sx + branchDir * subLen * 0.4, sy + subDir * subLen * 0.3,
                    sx + branchDir * subLen * 0.7, sy + subDir * subLen * 0.6,
                );
                gfx.stroke({ color: 0x0a1508, alpha: 0.35, width: 2 });
            }
        }

        // Expanded canopy — 5-7 ellipses with three-layer brightness
        const canopyCount = 5 + Math.floor(Math.random() * 3);
        for (let c = 0; c < canopyCount; c++) {
            const cx = tree.x + (Math.random() - 0.5) * tree.canopyW * 0.4;
            const cy = topY + c * 22 + Math.random() * 15;
            const layer = c < 2 ? 0 : c < 4 ? 1 : 2; // outer, middle, inner
            const alphas = [0.32, 0.38, 0.45];
            const colors = [0x081608, 0x0a1a08, 0x0c1e0a];
            const wMul = 1.0 - layer * 0.08;
            const hMul = 0.25 - c * 0.015;
            const isWideFlat = Math.random() > 0.4;
            gfx.ellipse(cx, cy,
                tree.canopyW * wMul * (isWideFlat ? 0.5 : 0.35),
                tree.canopyH * (isWideFlat ? hMul * 0.7 : hMul * 1.1),
            );
            gfx.fill({ color: colors[layer], alpha: alphas[layer] });
        }

        // Life on trees — shelf mushrooms
        for (let m = 0; m < 2 + Math.floor(Math.random() * 2); m++) {
            const mx = tree.x + (Math.random() > 0.5 ? 1 : -1) * (bw * 0.2 + Math.random() * bw * 0.1);
            const my = baseY * (0.35 + Math.random() * 0.35);
            // Semicircle shelf
            gfx.moveTo(mx - 8, my);
            gfx.quadraticCurveTo(mx, my - 7, mx + 8, my);
            gfx.closePath();
            gfx.fill({ color: 0x44aa44, alpha: 0.25 });
            // Tiny glow dot beneath
            gfx.circle(mx, my + 3, 2);
            gfx.fill({ color: 0x66ff66, alpha: 0.08 });
        }

        // Life on trees — moss patches
        for (let mp = 0; mp < 3 + Math.floor(Math.random() * 3); mp++) {
            const px = tree.x + (Math.random() - 0.5) * bw * 0.4;
            const py = baseY * (0.3 + Math.random() * 0.45);
            gfx.ellipse(px, py, 10 + Math.random() * 12, 5 + Math.random() * 5);
            gfx.fill({ color: 0x226633, alpha: 0.2 });
        }

        layers.deep.container.addChild(gfx);

        // Glow nodes — drawn as separate Graphics for per-frame alpha animation
        const nodeCount = 2 + Math.floor(Math.random() * 3);
        for (let n = 0; n < nodeCount; n++) {
            const nx = tree.x + (Math.random() - 0.5) * bw * 0.35;
            const ny = baseY * (0.25 + Math.random() * 0.45);
            const nodeGfx = new Graphics();
            // Halo
            nodeGfx.circle(nx, ny, 10);
            nodeGfx.fill({ color: 0x66ff66, alpha: 0.06 });
            // Core
            nodeGfx.circle(nx, ny, 3);
            nodeGfx.fill({ color: 0xaaffaa, alpha: 0.4 });
            layers.deep.container.addChild(nodeGfx);
            treeNodes.push({ gfx: nodeGfx, brightness: 0, phase: Math.random() * Math.PI * 2 });
        }
    }

    return treeNodes;
}

export function buildFernsAndGroundCover(layers: ParallaxLayers, W: number, H: number): void {
    // Tree base positions for large fern placement
    const treeBaseXs = [W * 0.03, W * 0.22, W * 0.42, W * 0.62, W * 0.82];

    // 8 small ferns — scattered
    for (let i = 0; i < 8; i++) {
        const fern = new Graphics();
        const fx = W * (0.05 + i * 0.12) + (Math.random() - 0.5) * 40;
        const fy = H * (0.7 + Math.random() * 0.1);
        for (let f = 0; f < 3; f++) {
            const angle = -Math.PI * 0.3 + f * Math.PI * 0.3;
            const fLen = 20 + Math.random() * 25;
            fern.moveTo(fx, fy);
            fern.quadraticCurveTo(
                fx + Math.cos(angle) * fLen * 0.6, fy + Math.sin(angle) * fLen * 0.6 - 10,
                fx + Math.cos(angle) * fLen, fy + Math.sin(angle) * fLen,
            );
        }
        fern.stroke({ color: 0x226622, alpha: 0.2, width: 1.5 });
        layers.deep.container.addChild(fern);
    }

    // 6 large ferns — placed at tree bases
    for (let i = 0; i < 6; i++) {
        const fern = new Graphics();
        const baseX = treeBaseXs[i % treeBaseXs.length];
        const fx = baseX + (Math.random() > 0.5 ? 1 : -1) * (40 + Math.random() * 50);
        const fy = H * (0.72 + Math.random() * 0.06);
        const frondCount = 4 + Math.floor(Math.random() * 2);
        for (let f = 0; f < frondCount; f++) {
            const angle = -Math.PI * 0.4 + f * (Math.PI * 0.8 / (frondCount - 1));
            const fLen = 40 + Math.random() * 40;
            fern.moveTo(fx, fy);
            fern.quadraticCurveTo(
                fx + Math.cos(angle) * fLen * 0.5, fy + Math.sin(angle) * fLen * 0.5 - 15,
                fx + Math.cos(angle) * fLen, fy + Math.sin(angle) * fLen,
            );
        }
        fern.stroke({ color: 0x2a7a2a, alpha: 0.22, width: 2.5 });
        layers.deep.container.addChild(fern);
    }

    // Moss carpet — 5 large flat ellipses at ground level
    for (let i = 0; i < 5; i++) {
        const moss = new Graphics();
        const mx = W * (0.1 + i * 0.2) + (Math.random() - 0.5) * 60;
        const my = H * 0.78 + Math.random() * 15;
        moss.ellipse(mx, my, 60 + Math.random() * 50, 8 + Math.random() * 6);
        moss.fill({ color: 0x1a4422, alpha: 0.18 });
        layers.deep.container.addChild(moss);
    }

    // Fallen logs — 3 horizontal curves with moss on top
    for (let i = 0; i < 3; i++) {
        const log = new Graphics();
        const lx = W * (0.15 + i * 0.3) + (Math.random() - 0.5) * 80;
        const ly = H * (0.76 + Math.random() * 0.04);
        const logLen = 60 + Math.random() * 50;
        // Log body
        log.moveTo(lx, ly);
        log.quadraticCurveTo(lx + logLen * 0.5, ly - 5 - Math.random() * 4, lx + logLen, ly + 2);
        log.stroke({ color: 0x0d1a0a, alpha: 0.4, width: 6 });
        // Moss on top
        log.ellipse(lx + logLen * 0.3, ly - 5, logLen * 0.25, 4);
        log.fill({ color: 0x226633, alpha: 0.2 });
        log.ellipse(lx + logLen * 0.65, ly - 4, logLen * 0.2, 3);
        log.fill({ color: 0x1a5528, alpha: 0.18 });
        layers.deep.container.addChild(log);
    }
}

export function buildVines(layers: ParallaxLayers, W: number, H: number): Vine[] {
    const vines: Vine[] = [];
    // 9 clusters of 5 vines each — curtain effect
    const clusterAnchors = [
        W * 0.06, W * 0.14, W * 0.25, W * 0.36, W * 0.47,
        W * 0.58, W * 0.69, W * 0.8, W * 0.92,
    ];

    for (const anchorX of clusterAnchors) {
        // Types: [0]=heavy, [1,2]=medium, [3,4]=tendril
        for (let v = 0; v < 5; v++) {
            const gfx = new Graphics();
            const spread = (v - 2) * 10 + (Math.random() - 0.5) * 20;
            const vx = anchorX + spread;
            const isHeavy = v === 0;
            const isMedium = v === 1 || v === 2;

            const strokeWidth = isHeavy ? 2.5 + Math.random() * 0.5 : isMedium ? 2 : 1;
            const vineColor = isHeavy ? 0x33aa33 : isMedium ? 0x33aa33 : 0x55bb55;
            const vineAlpha = isHeavy ? 0.3 : isMedium ? 0.25 : 0.18;

            const minLen = isHeavy ? 300 : isMedium ? 150 : 80;
            const maxLen = isHeavy ? 500 : isMedium ? 350 : 200;
            const vLen = minLen + Math.random() * (maxLen - minLen);
            const topY = Math.random() * 30;

            // Bezier vine
            const cp1x = vx + (Math.random() - 0.5) * 50;
            const cp1y = topY + vLen * 0.25;
            const cp2x = vx + (Math.random() - 0.5) * 35;
            const cp2y = topY + vLen * 0.65;
            const endX = vx + (Math.random() - 0.5) * 20;
            const endY = topY + vLen;

            gfx.moveTo(vx, topY);
            gfx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, endX, endY);
            gfx.stroke({ color: vineColor, alpha: vineAlpha, width: strokeWidth });

            // Glow structures along vine
            const glowCount = isHeavy ? 6 + Math.floor(Math.random() * 3) :
                isMedium ? 3 + Math.floor(Math.random() * 3) :
                    1 + Math.floor(Math.random() * 2);

            let hasFlower = false;
            for (let d = 0; d < glowCount; d++) {
                const t = (d + 1) / (glowCount + 1);
                const dotY = topY + vLen * t;
                const dotX = vx + (Math.random() - 0.5) * 12;

                // Bioluminescent flower — rare, only on heavy vines (1-2 max)
                if (isHeavy && !hasFlower && Math.random() < 0.35) {
                    hasFlower = true;
                    // 5-point star
                    for (let p = 0; p < 5; p++) {
                        const a = (p / 5) * Math.PI * 2 - Math.PI / 2;
                        const outerR = 5;
                        const innerR = 2;
                        const ox = dotX + Math.cos(a) * outerR;
                        const oy = dotY + Math.sin(a) * outerR;
                        const ia = a + Math.PI / 5;
                        const ix = dotX + Math.cos(ia) * innerR;
                        const iy = dotY + Math.sin(ia) * innerR;
                        if (p === 0) gfx.moveTo(ox, oy);
                        else gfx.lineTo(ox, oy);
                        gfx.lineTo(ix, iy);
                    }
                    gfx.closePath();
                    gfx.fill({ color: 0xaaffcc, alpha: 0.15 });
                    gfx.circle(dotX, dotY, 2);
                    gfx.fill({ color: 0xccffdd, alpha: 0.35 });
                } else if (isHeavy || isMedium) {
                    // Glow pod — egg-shaped (circle + ellipse beneath)
                    gfx.circle(dotX, dotY, 4);
                    gfx.fill({ color: 0x66ff66, alpha: 0.07 });
                    gfx.ellipse(dotX, dotY + 3, 3, 4);
                    gfx.fill({ color: 0x88ffaa, alpha: 0.05 });
                    gfx.circle(dotX, dotY, 1.5);
                    gfx.fill({ color: 0xaaffaa, alpha: 0.3 });
                } else {
                    // Standard dots for tendrils
                    gfx.circle(dotX, dotY, 4);
                    gfx.fill({ color: 0x66ff66, alpha: 0.05 });
                    gfx.circle(dotX, dotY, 1.2);
                    gfx.fill({ color: 0xaaffaa, alpha: 0.25 });
                }
            }

            // Leaf tip — two quadratic curves forming a small leaf
            const leafX = endX;
            const leafY = endY;
            gfx.moveTo(leafX, leafY);
            gfx.quadraticCurveTo(leafX - 4, leafY + 6, leafX, leafY + 10);
            gfx.quadraticCurveTo(leafX + 4, leafY + 6, leafX, leafY);
            gfx.fill({ color: 0x55cc55, alpha: 0.22 });

            layers.mid.container.addChild(gfx);
            vines.push({
                gfx, baseX: vx, topY, length: vLen,
                phase: Math.random() * Math.PI * 2,
                sway: 0,
            });
        }
    }

    return vines;
}
